import { getBackendBaseUrl } from './apiUtils';
import 'whatwg-fetch';

export const ambientRecordingStates = [
  'OFF',
  'INITIATING',
  'ACTIVE',
  'PAUSED',
  'FAILED',
  'ENDED',
  'STOPPED',
  'UNKNOWN',
] as const;

export type AmbientRecordingState = (typeof ambientRecordingStates)[number];

export interface AmbientRecordingStateResponse {
  state: AmbientRecordingState;
}

export class AmbientRecordingStateApiError extends Error {
  constructor(
    public readonly status: number | null,
    public readonly errorCode: string | null,
    public readonly retryAfterMilliseconds: number | null
  ) {
    super(`Ambient recording state request failed${status === null ? '' : ` with status ${status}`}`);
    this.name = 'AmbientRecordingStateApiError';
  }
}

const parseRetryAfter = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const dateMilliseconds = Date.parse(value);
  return Number.isNaN(dateMilliseconds) ? null : Math.max(0, dateMilliseconds - Date.now());
};

const getErrorCode = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as { errorCode?: string; errorMessage?: string; message?: string };
    return body.errorCode || body.errorMessage || body.message || null;
  } catch {
    return null;
  }
};

export const ambientRecordingStateApi = async (
  jwt: string,
  meetingNumber: string,
  signal?: AbortSignal
): Promise<AmbientRecordingStateResponse> => {
  let response: Response;
  try {
    response = await fetch(
      `${getBackendBaseUrl()}/ambient-recording-state?meetingNumber=${encodeURIComponent(meetingNumber)}`,
      {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${jwt}` }),
        signal,
      }
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new AmbientRecordingStateApiError(null, null, null);
  }

  if (!response.ok) {
    throw new AmbientRecordingStateApiError(
      response.status,
      await getErrorCode(response),
      parseRetryAfter(response.headers.get('Retry-After'))
    );
  }

  const body = (await response.json()) as Partial<AmbientRecordingStateResponse>;
  return {
    state: ambientRecordingStates.includes(body.state as AmbientRecordingState)
      ? (body.state as AmbientRecordingState)
      : 'UNKNOWN',
  };
};
